import { Briefcase, ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { PostingStatusText } from '../../applicants/components/PostingStatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import type { ApplicantScoreWeights, JobPostingListItem } from '../../applicants/applicantTypes';
import { DeliberationApplicantsPanel } from '../components/DeliberationApplicantsPanel';
import { deliberationApi } from '../deliberationApi';

type TabKey = 'info' | 'applicants';

/**
 * One job posting's DELIBERATION, on the **header** layout — the post it advertises, and a folder tab
 * of its applicants with every assessment stage's score side by side.
 *
 * **Nothing here is this module's to write**: the posting is Vacancies', the applicants and their
 * stages Applicants'. So the Post card links out, the header cog carries History Logs alone, and the
 * banner states the posting's state and whether Score Points are set — with no action, because the
 * fix for either is on another page under another key.
 */
export function DeliberationProfilePage() {
  const { postingId = '' } = useParams<{ postingId: string }>();
  const navigate = useNavigate();

  const [posting, setPosting] = useState<JobPostingListItem | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  /** Score Points — null while loading; a failed read leaves it null and the banner says nothing of it. */
  const [weights, setWeights] = useState<ApplicantScoreWeights | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // LANDS on Applicants (product call, 20 Oct 2026): a posting is opened here to weigh its applicants.
  const [tab, setTab] = useState<TabKey>('applicants');
  const [historyOpen, setHistoryOpen] = useState(false);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    deliberationApi
      .getPosting(postingId, controller.signal)
      .then((loaded) => {
        setPosting(loaded);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('The job posting could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [postingId, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();

    // The page still reads without them — the scores then show blank and the banner stays on the posting.
    deliberationApi
      .scoreWeights(controller.signal)
      .then(setWeights)
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    {
      key: 'applicants',
      label: (
        <>
          Applicants
          <CountBadge count={posting?.applicantCount ?? 0} tone="slate" />
        </>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Human Resources',
          'Recruitment',
          { label: 'Deliberate', to: '/deliberation' },
          ...(posting ? [posting.itemNumber] : []),
        ]}
      />

      {loadError && !posting ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !posting ? (
        <ProfileHeaderSkeleton tabs={tabs.length} />
      ) : (
        <ProfileHeaderShell<TabKey>
          id="deliberation-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          panel={tab === 'applicants' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="deliberation-profile-header"
              icon={<Briefcase className="h-5 w-5" />}
              title={posting.positionTitle}
              meta={[
                posting.itemNumber,
                <span key="unit" className="font-sans">
                  {posting.orgUnitName}
                </span>,
              ]}
              status={<PostingStatusText posting={posting} />}
              createdBy={posting.createdByName}
              createdAt={posting.createdAt}
              settings={{ onHistory: () => setHistoryOpen(true) }}
            />
          }
          banner={
            <ProfileBanner
              id="deliberation-profile-banner"
              tone={posting.isActive && !posting.isCompleted ? 'emerald' : 'slate'}
              message={bannerMessage(posting, weights)}
            />
          }
        >
          {tab === 'info' ? (
            <>
              <ProfileCard
                id="deliberation-profile-post"
                title="Post"
                // The plantilla's and Vacancies' record: no Edit here, links out to where it lives.
                menuItems={[
                  {
                    key: 'open-vacancy',
                    label: 'Open in Vacancies',
                    icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
                    permission: 'vacancies.read',
                    onClick: () => navigate(`/vacancies/${posting.plantillaItemId}`),
                  },
                ]}
              >
                <PreviewTable variant="table-display" rows={postRows(posting)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id="deliberation-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(posting)} />
              </ProfileCard>
            </>
          ) : (
            <DeliberationApplicantsPanel
              posting={posting}
              weights={weights}
              // An endorsement completes the posting and a withdrawal reopens it — the header and banner refetch.
              onChanged={reload}
            />
          )}
        </ProfileHeaderShell>
      )}

      {posting ? (
        <HistoryLogsModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          module="deliberations"
          // A RECORD's history is the id alone — every applicant added names the posting in its route.
          entityId={posting.id}
        />
      ) : null}
    </div>
  );
}

/** The posting's state, then whether its applicants can be scored at all. */
function bannerMessage(posting: JobPostingListItem, weights: ApplicantScoreWeights | null): string {
  const state = !posting.isActive
    ? posting.isFilled
      ? 'This posting has ended — its item is filled. Who holds it is under Applicants › Appointment; the applicants and their scores are kept.'
      : 'This posting has ended — its job posting tag was removed or the item was filled. Its applicants and their scores are kept.'
    : posting.isCompleted
      ? "This posting is completed — an endorsed applicant's appointment is recorded. It takes no new applicants; withdrawing the endorsement reopens it."
      : posting.applicantCount === 0
        ? 'Open for applications. Nobody has applied yet.'
        : `Open for applications. ${posting.applicantCount} applicant${posting.applicantCount === 1 ? '' : 's'} to deliberate on.`;

  return weights && !weights.isConfigured
    ? `${state} Score points are not set, so no stage is scored yet — they are set under Recruitment › Settings › Score Points.`
    : state;
}

/** What the header does not already say — title, item number and office are the header's. */
function postRows(posting: JobPostingListItem): PreviewRow[] {
  return [
    { label: 'Salary grade', value: posting.salaryGradeLabel },
    {
      label: 'Monthly salary',
      value: posting.monthlyAmount != null ? formatMoney(posting.monthlyAmount) : null,
      mono: true,
    },
    { label: 'Education', value: posting.education },
    { label: 'Training', value: posting.training },
    { label: 'Experience', value: posting.experience },
    { label: 'Eligibility', value: posting.eligibilityName },
    { label: 'Other expectation', value: posting.otherExpectations },
  ];
}

/** The update half — when the posting ended, and by whom. Created is the header's. */
function auditRows(posting: JobPostingListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: posting.updatedByName },
    { label: 'Updated', value: posting.updatedAt != null ? formatDateTime(posting.updatedAt) : null, mono: true },
  ];
}
